import { memo, useCallback, useEffect, useMemo, useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Box, CircularProgress, Skeleton, Typography } from "@mui/material";
import { resolveRowSection } from "./sectionData";

const ROW_HEIGHT = 36;
const HEADER_SECTION_HEIGHT = 40;
const HEADER_METRIC_HEIGHT = 52;
const BRAND_WIDTH = 120;
const DIVISION_WIDTH = 100;
const SECTION_COL_WIDTH = 112;
const MONTH_WIDTH = 88;
const DAY_WIDTH = 56;
const METRIC_WIDTH = 64;
const LOAD_MORE_THRESHOLD_PX = 200;
const SKELETON_ROW_COUNT = 10;

const PCT_METRIC_KEYS = new Set(["gmv_ach_pct", "gmv_goly_pct", "rsv_ach_pct", "rsv_goly_pct"]);

const headerBaseSx = {
	fontWeight: 700,
	fontSize: "0.68rem",
	whiteSpace: "nowrap",
	backgroundColor: "#f3f4f6",
	color: "#374151",
	borderBottom: "1px solid #d1d5db",
	borderRight: "1px solid #e5e7eb",
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	px: 0.75,
	boxSizing: "border-box",
};

const stickyLeftHeaderSx = (left, zIndex = 5) => ({
	...headerBaseSx,
	position: "sticky",
	left,
	zIndex,
	backgroundColor: "#f3f4f6",
});

const sectionHeaderSx = {
	...headerBaseSx,
	backgroundColor: "#e8eeff",
	borderBottom: "2px solid #94a3b8",
	minHeight: HEADER_SECTION_HEIGHT,
	height: HEADER_SECTION_HEIGHT,
};

const metricHeaderSx = {
	...headerBaseSx,
	fontSize: "0.62rem",
	whiteSpace: "normal",
	lineHeight: 1.2,
	textAlign: "center",
	minHeight: HEADER_METRIC_HEIGHT,
	height: HEADER_METRIC_HEIGHT,
};

const bodyCellSx = {
	fontSize: "0.68rem",
	color: "#111827",
	borderBottom: "1px solid #e5e7eb",
	borderRight: "1px solid #e5e7eb",
	display: "flex",
	alignItems: "center",
	px: 0.65,
	boxSizing: "border-box",
	height: ROW_HEIGHT,
	minHeight: ROW_HEIGHT,
};

const stickyLeftBodySx = (left) => ({
	...bodyCellSx,
	position: "sticky",
	left,
	zIndex: 2,
	backgroundColor: "#fff",
});

const formatCellValue = (value, metricKey) => {
	if (value === null || value === undefined || value === "") return "";
	const num = Number(value);
	if (Number.isNaN(num)) return String(value);
	if (PCT_METRIC_KEYS.has(metricKey)) {
		return Number.isInteger(num) ? `${num}%` : `${num.toFixed(2)}%`;
	}
	return Number.isInteger(num) ? String(num) : num.toFixed(2);
};

const rowKey = (row) => `${row.brand}|${row.division ?? ""}|${row.section ?? ""}|${row.date}`;

const buildGridTemplateColumns = (metricColumnCount) =>
	`${BRAND_WIDTH}px ${DIVISION_WIDTH}px ${SECTION_COL_WIDTH}px ${MONTH_WIDTH}px ${DAY_WIDTH}px repeat(${metricColumnCount}, ${METRIC_WIDTH}px)`;

const SkeletonRow = memo(function SkeletonRow({ gridTemplateColumns, metricColumnCount, metricsPerSection }) {
	return (
		<Box
			sx={{
				display: "grid",
				gridTemplateColumns,
				width: "max-content",
				minWidth: "100%",
				height: ROW_HEIGHT,
				borderBottom: "1px solid #e5e7eb",
			}}>
			<Box sx={{ ...bodyCellSx, justifyContent: "flex-start" }}>
				<Skeleton variant="text" width="75%" height={18} />
			</Box>
			<Box sx={{ ...bodyCellSx, justifyContent: "flex-start" }}>
				<Skeleton variant="text" width="65%" height={18} />
			</Box>
			<Box sx={{ ...bodyCellSx, justifyContent: "flex-start" }}>
				<Skeleton variant="text" width="60%" height={18} />
			</Box>
			<Box sx={{ ...bodyCellSx, justifyContent: "flex-start" }}>
				<Skeleton variant="text" width="55%" height={18} />
			</Box>
			<Box sx={{ ...bodyCellSx, justifyContent: "center", borderRight: "2px solid #e5e7eb" }}>
				<Skeleton variant="text" width="40%" height={18} />
			</Box>
			{Array.from({ length: metricColumnCount }).map((_, j) => (
				<Box
					key={j}
					sx={{
						...bodyCellSx,
						justifyContent: "flex-end",
						...(metricsPerSection > 0 && (j + 1) % metricsPerSection === 0
							? { borderRight: "2px solid #e5e7eb" }
							: {}),
					}}>
					<Skeleton variant="text" width="65%" height={18} sx={{ ml: "auto" }} />
				</Box>
			))}
		</Box>
	);
});

const VirtualDataRow = memo(function VirtualDataRow({
	row,
	columns,
	gridTemplateColumns,
	stickyOffsets,
	divisionFallback = "",
	sectionFallback = "",
}) {
	return (
		<Box
			sx={{
				display: "grid",
				gridTemplateColumns,
				width: "max-content",
				minWidth: "100%",
				height: ROW_HEIGHT,
				"&:hover > div": { backgroundColor: "#f9fafb" },
			}}>
			<Box sx={{ ...stickyLeftBodySx(stickyOffsets.brand), fontWeight: 500, justifyContent: "flex-start" }}>
				{row.brand}
			</Box>
			<Box sx={{ ...stickyLeftBodySx(stickyOffsets.division), justifyContent: "flex-start" }}>
				{row.division ?? divisionFallback ?? ""}
			</Box>
			<Box sx={{ ...stickyLeftBodySx(stickyOffsets.section), justifyContent: "flex-start" }}>
				{resolveRowSection(row, sectionFallback)}
			</Box>
			<Box sx={{ ...stickyLeftBodySx(stickyOffsets.month), justifyContent: "flex-start" }}>{row.month}</Box>
			<Box
				sx={{
					...stickyLeftBodySx(stickyOffsets.day),
					justifyContent: "center",
					borderRight: "2px solid #e5e7eb",
				}}>
				{row.day}
			</Box>
			{columns.flatMap((col) =>
				col.metrics.map((metric, metricIdx) => {
					const raw = row.sections?.[col.key]?.[metric.key];
					return (
						<Box
							key={`${col.key}-${metric.key}`}
							sx={{
								...bodyCellSx,
								justifyContent: "flex-end",
								...(metricIdx === col.metrics.length - 1 ? { borderRight: "2px solid #e5e7eb" } : {}),
							}}>
							{formatCellValue(raw, metric.key)}
						</Box>
					);
				})
			)}
		</Box>
	);
});

const PivotHeader = ({ columns, gridTemplateColumns, stickyOffsets, showDynamicColumns }) => {
	const fixedOnlyGrid = buildGridTemplateColumns(0);

	if (!showDynamicColumns) {
		return (
			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: fixedOnlyGrid,
					position: "sticky",
					top: 0,
					zIndex: 6,
					width: "max-content",
					minWidth: "100%",
					borderBottom: "2px solid #94a3b8",
				}}>
				<Box sx={{ ...stickyLeftHeaderSx(stickyOffsets.brand, 7), justifyContent: "flex-start", minHeight: HEADER_SECTION_HEIGHT }}>
					Brand
				</Box>
				<Box sx={{ ...stickyLeftHeaderSx(stickyOffsets.division, 7), justifyContent: "flex-start", minHeight: HEADER_SECTION_HEIGHT }}>
					Division
				</Box>
				<Box sx={{ ...stickyLeftHeaderSx(stickyOffsets.section, 7), justifyContent: "flex-start", minHeight: HEADER_SECTION_HEIGHT }}>
					Section
				</Box>
				<Box sx={{ ...stickyLeftHeaderSx(stickyOffsets.month, 7), justifyContent: "flex-start", minHeight: HEADER_SECTION_HEIGHT }}>
					Month
				</Box>
				<Box
					sx={{
						...stickyLeftHeaderSx(stickyOffsets.day, 7),
						justifyContent: "center",
						borderRight: "2px solid #94a3b8",
						minHeight: HEADER_SECTION_HEIGHT,
					}}>
					Day
				</Box>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				display: "grid",
				gridTemplateColumns,
				gridTemplateRows: `${HEADER_SECTION_HEIGHT}px ${HEADER_METRIC_HEIGHT}px`,
				position: "sticky",
				top: 0,
				zIndex: 6,
				width: "max-content",
				minWidth: "100%",
			}}>
			<Box
				sx={{
					...stickyLeftHeaderSx(stickyOffsets.brand, 7),
					gridRow: "span 2",
					justifyContent: "flex-start",
					alignSelf: "stretch",
					borderBottom: "2px solid #94a3b8",
				}}>
				Brand
			</Box>
			<Box
				sx={{
					...stickyLeftHeaderSx(stickyOffsets.division, 7),
					gridRow: "span 2",
					justifyContent: "flex-start",
					alignSelf: "stretch",
					borderBottom: "2px solid #94a3b8",
				}}>
				Division
			</Box>
			<Box
				sx={{
					...stickyLeftHeaderSx(stickyOffsets.section, 7),
					gridRow: "span 2",
					justifyContent: "flex-start",
					alignSelf: "stretch",
					borderBottom: "2px solid #94a3b8",
				}}>
				Section
			</Box>
			<Box
				sx={{
					...stickyLeftHeaderSx(stickyOffsets.month, 7),
					gridRow: "span 2",
					justifyContent: "flex-start",
					alignSelf: "stretch",
					borderBottom: "2px solid #94a3b8",
				}}>
				Month
			</Box>
			<Box
				sx={{
					...stickyLeftHeaderSx(stickyOffsets.day, 7),
					gridRow: "span 2",
					justifyContent: "center",
					alignSelf: "stretch",
					borderRight: "2px solid #94a3b8",
					borderBottom: "2px solid #94a3b8",
				}}>
				Day
			</Box>
			{columns.map((col) => (
				<Box
					key={col.key}
					sx={{
						...sectionHeaderSx,
						gridColumn: `span ${col.metrics.length}`,
						borderRight: "2px solid #94a3b8",
					}}>
					{col.label}
				</Box>
			))}
			{columns.flatMap((col) =>
				col.metrics.map((metric, metricIdx) => (
					<Box
						key={`${col.key}-${metric.key}`}
						sx={{
							...metricHeaderSx,
							...(metricIdx === col.metrics.length - 1 ? { borderRight: "2px solid #94a3b8" } : {}),
						}}>
						{metric.label}
					</Box>
				))
			)}
		</Box>
	);
};

const PivotVirtualTable = ({
	columns = [],
	rows = [],
	loadingInitial = false,
	loadingMore = false,
	hasMore = false,
	onLoadMore,
	loadedCount = 0,
	totalCount = 0,
	reportRequested = false,
	divisionFallback = "",
	sectionFallback = "",
}) => {
	const scrollRef = useRef(null);
	const loadMoreLockRef = useRef(false);

	const metricsPerSection = columns[0]?.metrics?.length ?? 0;
	const metricColumnCount = columns.length * metricsPerSection;
	const showDynamicColumns = columns.length > 0;

	const gridTemplateColumns = useMemo(
		() => buildGridTemplateColumns(metricColumnCount),
		[metricColumnCount]
	);

	const stickyOffsets = useMemo(
		() => ({
			brand: 0,
			division: BRAND_WIDTH,
			section: BRAND_WIDTH + DIVISION_WIDTH,
			month: BRAND_WIDTH + DIVISION_WIDTH + SECTION_COL_WIDTH,
			day: BRAND_WIDTH + DIVISION_WIDTH + SECTION_COL_WIDTH + MONTH_WIDTH,
		}),
		[]
	);

	const rowVirtualizer = useVirtualizer({
		count: rows.length,
		getScrollElement: () => scrollRef.current,
		estimateSize: () => ROW_HEIGHT,
		overscan: 12,
		getItemKey: (index) => rowKey(rows[index]),
	});

	const tryLoadMore = useCallback(() => {
		if (!hasMore || loadingMore || loadingInitial || loadMoreLockRef.current) return;
		const el = scrollRef.current;
		if (!el) return;
		const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
		if (distanceFromBottom > LOAD_MORE_THRESHOLD_PX) return;
		loadMoreLockRef.current = true;
		onLoadMore?.();
	}, [hasMore, loadingMore, loadingInitial, onLoadMore]);

	useEffect(() => {
		if (!loadingMore) {
			loadMoreLockRef.current = false;
		}
	}, [loadingMore]);

	useEffect(() => {
		const el = scrollRef.current;
		if (!el) return undefined;
		const onScroll = () => tryLoadMore();
		el.addEventListener("scroll", onScroll, { passive: true });
		return () => el.removeEventListener("scroll", onScroll);
	}, [tryLoadMore]);

	useEffect(() => {
		if (rows.length > 0) tryLoadMore();
	}, [rows.length, hasMore, tryLoadMore]);

	const virtualItems = rowVirtualizer.getVirtualItems();
	const showEmpty = !reportRequested && !loadingInitial;
	const showSkeleton = loadingInitial && rows.length === 0;
	const skeletonGrid = showDynamicColumns ? gridTemplateColumns : buildGridTemplateColumns(0);
	const skeletonMetricCount = showDynamicColumns ? metricColumnCount : 0;

	return (
		<Box sx={{ width: "100%", overflow: "hidden", border: "1px solid #e5e7eb", borderRadius: 1 }}>
			<Box
				ref={scrollRef}
				sx={{
					maxHeight: "calc(100vh - 340px)",
					overflow: "auto",
					position: "relative",
				}}>
				<Box sx={{ width: "max-content", minWidth: "100%" }}>
					{(reportRequested || rows.length > 0) && (
						<PivotHeader
							columns={columns}
							gridTemplateColumns={gridTemplateColumns}
							stickyOffsets={stickyOffsets}
							showDynamicColumns={showDynamicColumns}
						/>
					)}

					{showSkeleton ? (
						<Box>
							{Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
								<SkeletonRow
									key={`skel-${i}`}
									gridTemplateColumns={skeletonGrid}
									metricColumnCount={skeletonMetricCount}
									metricsPerSection={metricsPerSection}
								/>
							))}
						</Box>
					) : null}

					{showEmpty ? (
						<Box sx={{ py: 5, textAlign: "center" }}>
							<Typography variant="body2" color="text.secondary">
								Select filters and click Load Report to view data.
							</Typography>
						</Box>
					) : null}

					{!showSkeleton && rows.length === 0 && reportRequested && !loadingInitial ? (
						<Box sx={{ py: 5, textAlign: "center" }}>
							<Typography variant="body2" color="text.secondary">
								No data found for the selected filters.
							</Typography>
						</Box>
					) : null}

					{rows.length > 0 ? (
						<Box
							sx={{
								height: rowVirtualizer.getTotalSize(),
								position: "relative",
								width: "max-content",
								minWidth: "100%",
							}}>
							{virtualItems.map((virtualRow) => {
								const row = rows[virtualRow.index];
								return (
									<Box
										key={virtualRow.key}
										data-index={virtualRow.index}
										ref={rowVirtualizer.measureElement}
										sx={{
											position: "absolute",
											top: 0,
											left: 0,
											width: "max-content",
											minWidth: "100%",
											transform: `translateY(${virtualRow.start}px)`,
										}}>
										<VirtualDataRow
											row={row}
											columns={columns}
											gridTemplateColumns={gridTemplateColumns}
											stickyOffsets={stickyOffsets}
											divisionFallback={divisionFallback}
											sectionFallback={sectionFallback}
										/>
									</Box>
								);
							})}
						</Box>
					) : null}
				</Box>
			</Box>

			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: 2,
					px: 1.5,
					py: 1,
					borderTop: "1px solid #e5e7eb",
					backgroundColor: "#fafafa",
				}}>
				<Typography variant="caption" color="text.secondary">
					{loadingInitial
						? "Loading report…"
						: totalCount > 0
							? `Loaded ${loadedCount} of ${totalCount} day(s)`
							: loadedCount > 0
								? `Loaded ${loadedCount} row(s)`
								: reportRequested
									? "No rows loaded"
									: "Select filters and load report"}
					{hasMore ? " · scroll for more" : loadedCount > 0 ? " · all loaded" : ""}
				</Typography>
				{loadingMore || loadingInitial ? <CircularProgress size={18} /> : null}
			</Box>
		</Box>
	);
};

export default PivotVirtualTable;
